/**
 * PermissionsTab - Single home for everything permission-related in the Admin panel.
 *
 * Primary row: Access Control V3 (industry-standard modules × levels × templates,
 * per-project assignments, enforced exceptions, explainer) + plain-English help.
 * Secondary row: the legacy V2 system (still what the live routes enforce today)
 * kept visible until the cutover is complete.
 *
 * Sub-tab is persisted in the URL (?tab=permissions&sub=...) so views can be
 * shared with other superadmins by link.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PermissionsHelpTab from './PermissionsHelpTab';
import PermissionsDashboard from '../Development/PermissionsDashboard'; // pragma: allowlist secret (folder name, not a secret)
import PermissionMatrixTab from './PermissionMatrixTab';
import RolesConfigTab from './RolesConfigTab';
import AuthorityLevelsTab from './AuthorityLevelsTab';
import PermissionOverridesTab from './PermissionOverridesTab';
import PermissionTesterTab from './PermissionTesterTab';
import AccessTemplatesTab from './AccessTemplatesTab';
import AccessAssignmentsTab from './AccessAssignmentsTab';
import AccessExceptionsTab from './AccessExceptionsTab';
import AccessExplainerTab from './AccessExplainerTab';

const PRIMARY_TABS = [
  { id: 'help', label: 'How It Works', icon: '❓', desc: 'Plain-English explanation of the whole system' },
  { id: 'templates', label: 'Templates', icon: '🧩', desc: 'Per-module access levels bundled into named templates' },
  { id: 'assignments', label: 'Assignments', icon: '🎩', desc: 'Company default + a different template per project' },
  { id: 'exceptions', label: 'Exceptions', icon: '🎫', desc: 'Per-person grants/denials — enforced, deny wins' },
  { id: 'explainer', label: 'Explainer', icon: '🔍', desc: 'Why can (or can’t) this person do X?' },
];

const LEGACY_TABS = [
  { id: 'overview', label: 'Overview', icon: '🛡️', desc: 'Read-only map of the legacy system' },
  { id: 'matrix', label: 'Role Matrix', icon: '📋', desc: 'Legacy roles × permissions grid' },
  { id: 'roles', label: 'Roles', icon: '👷', desc: 'Legacy role template editor' },
  { id: 'authority', label: 'Authority Levels', icon: '🔑', desc: 'Legacy rank editor' },
  { id: 'overrides', label: 'Overrides', icon: '⚠️', desc: 'Legacy overrides (never enforced)' },
  { id: 'tester', label: 'Tester', icon: '🧪', desc: 'Legacy permission simulator' },
];

const ALL_TAB_IDS = new Set([...PRIMARY_TABS, ...LEGACY_TABS].map(t => t.id));

const tabButton = (tab, active, onClick) => (
  <button
    key={tab.id}
    onClick={onClick}
    title={tab.desc}
    style={{
      padding: '0.5rem 1rem',
      border: 'none',
      backgroundColor: active ? '#007bff' : '#f8f9fa',
      color: active ? 'white' : '#666',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: active ? '500' : '400',
      display: 'flex', alignItems: 'center', gap: 6,
    }}
  >
    <span>{tab.icon}</span> {tab.label}
  </button>
);

const PermissionsTab = ({ permissions }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeSubTab, setActiveSubTabState] = useState(() => {
    const fromUrl = searchParams.get('sub');
    if (fromUrl && ALL_TAB_IDS.has(fromUrl)) return fromUrl;
    // Old bookmarks to the removed top-level tabs land on the matching sub-tab.
    const legacyTab = searchParams.get('tab');
    if (legacyTab === 'roles') return 'roles';
    if (legacyTab === 'authority-levels') return 'authority';
    return 'help';
  });

  const setActiveSubTab = (sub) => {
    setActiveSubTabState(sub);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'permissions');
    next.set('sub', sub);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>Permissions</h3>
          <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: '0.85rem' }}>
            Access Control V3 (templates × levels × per-project hats) — plus the legacy system until cutover.
          </p>
        </div>
      </div>

      {/* New system */}
      <div style={{
        display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center',
        marginBottom: '0.5rem',
      }}>
        {PRIMARY_TABS.map(tab => tabButton(tab, activeSubTab === tab.id, () => setActiveSubTab(tab.id)))}
      </div>

      {/* Legacy strip */}
      <div style={{
        display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e9ecef',
      }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#adb5bd', textTransform: 'uppercase', letterSpacing: 1, marginRight: 4 }}>
          Legacy (live system until cutover):
        </span>
        {LEGACY_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            title={tab.desc}
            style={{
              padding: '0.3rem 0.7rem',
              border: '1px solid #e9ecef',
              backgroundColor: activeSubTab === tab.id ? '#6c757d' : '#fff',
              color: activeSubTab === tab.id ? 'white' : '#868e96',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.78rem',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'help' && <PermissionsHelpTab />}
      {activeSubTab === 'templates' && <AccessTemplatesTab />}
      {activeSubTab === 'assignments' && <AccessAssignmentsTab />}
      {activeSubTab === 'exceptions' && <AccessExceptionsTab />}
      {activeSubTab === 'explainer' && <AccessExplainerTab />}

      {activeSubTab === 'overview' && <PermissionsDashboard embedded />}
      {activeSubTab === 'matrix' && <PermissionMatrixTab />}
      {activeSubTab === 'roles' && <RolesConfigTab isSuperAdmin={permissions?.isSuperadmin} />}
      {activeSubTab === 'authority' && <AuthorityLevelsTab isSuperadmin={permissions?.isSuperadmin} />}
      {activeSubTab === 'overrides' && (
        <div>
          <div style={{
            marginBottom: '1rem', padding: '10px 14px', borderRadius: 8,
            backgroundColor: '#fff8e6', border: '1px solid #f0c36d',
            fontSize: '0.85rem', color: '#7a5c00', lineHeight: 1.6,
          }}>
            <strong>Legacy overrides are not enforced</strong> and are replaced by the new
            <strong> Exceptions</strong> tab (which is enforced). This screen stays only for reference
            until the old system is removed.
          </div>
          <PermissionOverridesTab />
        </div>
      )}
      {activeSubTab === 'tester' && <PermissionTesterTab />}
    </div>
  );
};

export default PermissionsTab;
